import {
  ensureEditableCaretForInsert,
  tryDeleteAdjacentAtomic,
} from '../../../DocEditor/utils/atomicCaret';
import { separateNextUndoStep } from '../../../DocEditor/utils/undoSeparation';
import { hasMathSlash } from '../../mathSlash';
import { isSlashBoundary, slashQueryFrom } from '../../valueSlash';
import { getTextBeforeCaret } from '../utils/caret';
import { scheduleClipboardWrite } from '../utils/largeValueGesture';

/** Keystrokes, the slash query, and replacing a fully selected heavy value. */
export function useNestedTyping({
  elRef,
  readOnly,
  slashAllowed,
  slashOpenRef,
  mathMenuOpenRef,
  pickerOpenRef,
  openAt,
  setQuery,
  closeSlash,
  caretMenuPosition,
  openMathAtCaret,
  openPickerAtCaret,
  allSelectedRef,
  heavyField,
  ownsWholeValue,
  selectEntireValue,
  clearAllSelected,
  clearStored,
  writeStored,
  readFull,
}) {
  const handleInput = () => {
    if (readOnly) return;
    const before = getTextBeforeCaret(elRef.current);
    if (slashAllowed?.length) {
      const justTypedSlash =
        !slashOpenRef.current &&
        before.endsWith('/') &&
        isSlashBoundary(before.slice(0, -1));
      if (justTypedSlash) {
        openAt(caretMenuPosition());
      } else if (slashOpenRef.current) {
        const query = slashQueryFrom(before);
        if (query == null) closeSlash();
        else setQuery(query);
      }
    }
    if (hasMathSlash(before) && !mathMenuOpenRef.current) {
      closeSlash();
      openMathAtCaret();
      return;
    }
    if (before.endsWith('{{') && !pickerOpenRef.current) {
      openPickerAtCaret();
    }
  };

  const handleKeyDown = (e) => {
    if (readOnly) return;
    if (mathMenuOpenRef.current || slashOpenRef.current) return;

    const key = e.key.toLowerCase();
    const mod = e.ctrlKey || e.metaKey;
    if (mod && key === 'a' && !e.altKey && !e.shiftKey && heavyField()) {
      e.preventDefault();
      e.stopPropagation();
      selectEntireValue();
      return;
    }
    if (ownsWholeValue() && mod && (key === 'x' || key === 'c') && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (key === 'c') {
        scheduleClipboardWrite(readFull());
        return;
      }
      separateNextUndoStep();
      const text = readFull();
      clearStored();
      scheduleClipboardWrite(text);
      return;
    }
    if (
      ownsWholeValue() &&
      (e.key === 'Backspace' || e.key === 'Delete') &&
      !mod &&
      !e.altKey
    ) {
      e.preventDefault();
      e.stopPropagation();
      separateNextUndoStep();
      clearStored();
      return;
    }
    if (
      allSelectedRef.current &&
      e.key.length === 1 &&
      !mod &&
      !e.altKey
    ) {
      e.preventDefault();
      separateNextUndoStep();
      writeStored(e.key);
      return;
    }
    if (allSelectedRef.current && (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' || e.key === 'Escape')) {
      clearAllSelected();
    }

    if (tryDeleteAdjacentAtomic(e)) return;
    // Enter exits the cell; Shift+Enter keeps a newline for long values
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (hasMathSlash(getTextBeforeCaret(elRef.current))) {
        openMathAtCaret();
        return;
      }
      e.currentTarget.blur();
    }
  };

  const handleBeforeInput = (e) => {
    if (readOnly) return;
    if (ownsWholeValue()) {
      const type = e.inputType || '';
      if (type.startsWith('delete')) {
        e.preventDefault();
        separateNextUndoStep();
        if (type === 'deleteByCut') scheduleClipboardWrite(readFull());
        clearStored();
        return;
      }
      if (type === 'insertText' || type === 'insertCompositionText') {
        e.preventDefault();
        separateNextUndoStep();
        writeStored(e.data || '');
        return;
      }
    }
    ensureEditableCaretForInsert();
  };

  return { handleInput, handleKeyDown, handleBeforeInput };
}
